import { useEffect, useRef } from 'react';
import {
  useAcceptInviteMutation,
  useReconcileMembershipMutation,
} from '@api/endpoints/onboardingApi';
import { useAuth } from '@auth/AuthContext';

const INVITE_TOKEN_KEY = 'medrender_invite_token';

/**
 * Silently activates an invited user's membership after they authenticate.
 *
 * Two paths, both server-authoritative:
 *
 *  1. Token path (happy path): SignupPage stores the invite token from the URL
 *     in localStorage; after signup → verification → login this reads the token
 *     and calls POST /practice/accept-invite.
 *
 *  2. Reconcile safety net (durable fix): the token path can silently fail to
 *     run — the token is lost across the signup → verify → login journey (a
 *     different browser/device, a Hosted-UI redirect, or cleared storage), which
 *     strands the user with no role and no membership on /onboarding-path. So
 *     whenever an authenticated user has NO role group, we also call
 *     POST /practice/reconcile-membership, which matches a pending invite by the
 *     caller's Cognito-VERIFIED email and activates it server-side. This is
 *     idempotent (a no-op once a membership exists) and needs no client token.
 *
 * On success (either path) the Cognito session is force-refreshed so the ID
 * token carries the newly assigned group, then the page reloads to pick up the
 * new membership and role.
 *
 * Renders nothing — this is a side-effect-only component.
 */
export default function AcceptInviteHandler() {
  const { user, forceRefreshSession } = useAuth();
  const [acceptInvite] = useAcceptInviteMutation();
  const [reconcileMembership] = useReconcileMembershipMutation();
  const attempted = useRef(false);

  useEffect(() => {
    if (!user || attempted.current) return;

    // A user who already has a role group is fully provisioned — nothing to do.
    // This keeps the reconcile call off the hot path for healthy logins.
    const groups = user.groups ?? [];
    if (groups.length > 0) return;

    attempted.current = true;

    const finishActivated = async () => {
      // Force-refresh the Cognito session so the ID token includes the newly
      // assigned group, then reload to pick up the new membership and role.
      try {
        await forceRefreshSession();
      } catch (e) {
        console.error('Failed to refresh session after activation:', e);
      }
      window.location.reload();
    };

    // Server-side reconciliation by verified email — the token-independent
    // safety net. Only reloads when it actually activated a membership.
    const runReconcile = () =>
      reconcileMembership()
        .unwrap()
        .then((res) => {
          if (res?.reconciled || res?.membership) {
            return finishActivated();
          }
        })
        .catch((err) => {
          console.error('Membership reconciliation failed:', err);
        });

    const token = localStorage.getItem(INVITE_TOKEN_KEY);
    if (token) {
      acceptInvite({
        inviteToken: token,
        email: user.email ?? undefined,
        name: user.name ?? undefined,
      })
        .unwrap()
        .then(async () => {
          localStorage.removeItem(INVITE_TOKEN_KEY);
          await finishActivated();
        })
        .catch(async (err) => {
          console.error('Failed to accept invite by token; falling back to reconcile:', err);
          // Clear the token so we don't retry the failing token path, then fall
          // back to the durable email-based reconcile.
          localStorage.removeItem(INVITE_TOKEN_KEY);
          await runReconcile();
        });
      return;
    }

    // No token — go straight to server-side reconciliation.
    void runReconcile();
  }, [user, acceptInvite, reconcileMembership, forceRefreshSession]);

  return null;
}
